import { BucketId } from "../types/File";

/**
 * Public (CDN) domain for each bucket's files. The two buckets are served from
 * distinct domains; overridable at build time so a single build works on both
 * subdomains.
 */
const PUBLIC_URLS: Record<BucketId, string> = {
  marketing: process.env.REACT_APP_MARKETING_PUBLIC_URL || "https://marketing-files.decentraland.org",
  external: process.env.REACT_APP_EXTERNAL_PUBLIC_URL || "https://external-files.decentraland.org",
};

/**
 * Constructs a public URL for a file using the given bucket's public domain.
 * @param key - The file key from R2
 * @param bucket - Which bucket the file belongs to
 * @returns The public URL for the file
 */
export const constructPublicUrl = (key: string, bucket: BucketId): string => {
  const base = PUBLIC_URLS[bucket].replace(/\/$/, "");
  return `${base}/${key}`;
};

/**
 * Creates safe HTTP headers by filtering out any problematic values
 * @param baseHeaders - The base headers to include
 * @returns Safe headers object with only valid, safe header values
 */
export const createSafeHeaders = (baseHeaders: Record<string, string>): Record<string, string> => {
  const safeHeaders: Record<string, string> = {};
  
  for (const [key, value] of Object.entries(baseHeaders)) {
    // Filter to only allow basic safe characters
    if (value && 
        typeof value === 'string' && 
        value.length > 0 && 
        value.length < 500 &&
        /^[a-zA-Z0-9\s\-_.,:;!@#$%^&*()+=<>?[\]{}|~`"']*$/.test(value)) {
      safeHeaders[key] = value;
    }
  }
  
  return safeHeaders;
};

/**
 * Creates minimal safe headers with only essential, safe values
 * This is a more aggressive approach that only includes known safe headers
 * @param baseHeaders - The base headers to include
 * @returns Minimal headers object with only safe, essential headers
 */
export const createMinimalSafeHeaders = (baseHeaders: Record<string, string>): Record<string, string> => {
  const minimalHeaders: Record<string, string> = {};
  
  // Only include headers that we explicitly know are safe
  const safeHeaderKeys = ['Authorization', 'Accept', 'Content-Type'];
  
  for (const key of safeHeaderKeys) {
    if (baseHeaders[key] && 
        typeof baseHeaders[key] === 'string' && 
        baseHeaders[key].length > 0 && 
        baseHeaders[key].length < 500) {
      minimalHeaders[key] = baseHeaders[key];
    }
  }
  
  return minimalHeaders;
}; 